<template>
  <div class="swiper-item">
    <div
      class="scale-item"
      :class="{ 'show-collection': showNewUI }"
    >
      <div class="video-poster">
        <video-player
          v-if="isCreatePlayer"
          ref="playerRef"
          :feed="feed"
          :show-play-btn="showPlayBtn"
          :show-play-status="!isDrama"
          :options="videoOptions"
          :play-icon-click="playIconClick"
          :mini-app-path-click-play="miniAppPathClickPlay"
          :show-mask="!isDrama"
          :show-cover="showCover"
          @on-play="onPlay"
          @on-playing="onPlaying"
          @on-pause="onPause"
          @on-timeupdate="onTimeupdate"
          @on-ended="onEnded"
          @on-prevent-autoplay="onPreventAutoplay"
          @on-click-playStatus="onClickPlayStatus"
        />
      </div>
      <div
        v-if="actionWrapperExist"
        class="tools-wrapper"
      >
        <transition name="fade">
          <div
            v-show="!showCollection"
            class="animate"
          >
            <player-info
              :feed="feed"
              :is-play="isPlay"
              :current-time="currentTime"
              @on-click-collection="onClickCollection"
            />
            <player-progress :progress="progress" />
          </div>
        </transition>
        <div
          v-if="(initOldUI || initPlayRec)"
          class="show-btn"
          @click="clickShowBtn"
        >
          <div>{{ showCollection ? '收起' : '更多视频' }}<i :class="{ show: showCollection }" /></div>
        </div>
      </div>
      <div
        v-if="isDrama"
        class="drama-act-wrapper"
        @click="clickDramaWrap"
      >
        <div class="drama-act">
          <div
            v-show="showCover"
            class="drama-cover"
            :style="{ backgroundImage: getImage(activeDrama?.coverImg) }"
          />
          <div
            v-show="showDramaAct || showCover"
            class="act-area"
            @click="clickDramaBtn"
          >
            <div
              v-if="!isDragging && !isPlay"
              class="act-btn play"
            />
            <div
              v-if="!isDragging && isPlay"
              class="act-btn pause"
            />
            <div
              v-if="isDragging"
              class="drag-time"
            >
              <span class="current-time">{{ formatTime(currentTime) }}</span>
              /
              <span class="total-time">{{ formatTime(totalTime) }}</span>
            </div>
          </div>
          <div
            v-show="showDramaAct"
            class="show-detail"
          >
            <div class="name">
              {{ activeDrama?.name }}
            </div>
            <div class="brief">
              {{ activeDrama?.brief }}
            </div>
            <div class="progress-wrap">
              <div class="time-display">
                <span>{{ formatTime(currentTime) }}/</span>
                <span>{{ formatTime(totalTime) }}</span>
              </div>
              <div
                class="progress-container"
                @touchstart="touchProgressStart"
                @touchmove="touchProgressMove"
                @touchend="touchProgressEnd"
              >
                <div
                  ref="progressRef"
                  class="progress"
                >
                  <div
                    class="progress-filled"
                    :style="{ width: `${playPercent}%` }"
                  />
                  <div
                    class="progress-handle"
                    :style="{ left: `${playPercent}%` }"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <transition name="slide">
      <CollectionDesc
        v-show="showNewUI"
        :feed="feed"
      />
    </transition>
  </div>
</template>

<script lang="ts" setup>
import { toRefs, computed, ref, watch, onMounted } from 'vue';
import { useStore, MutationTypes } from '@/store/index';
import { VideoJsPlayer } from 'video.js';
import { ModelMetaFeed } from '@/types/model';
import { EMiniAppPosition } from '@/constants/mini-app';
import useGetCommonMethods from '@/composable/use-get-common-method';
import useVideoEventHandler from '@/composable/use-video-event-handler';
import useProvideUrlInfo from '@/composable/use-provide-url-info';
import { userSwiperItemReport, useDramaReport } from '@/composable/use-compoent-report-methods';
import { EVideoEvtNameTypes } from '@/constants/video';
import { formatTime } from '@/utils/time';
import { fixHttps } from '@/utils/no-import-meta/url';

import PlayerInfo from './player-info-new.vue';
import CollectionDesc from './collection-desc.vue';
import VideoPlayer from './common/video-player-new.vue';
import PlayerProgress from './common/player-progress.vue';

const emits = defineEmits(['on-ended']);
// props
interface Props {
  feed: ModelMetaFeed,
  isLastFeed: boolean,
  initPlayRec: boolean,
  playIconClick: Function,
  showRecInPlayPage: boolean,
}
const props = withDefaults(defineProps<Props>(), {
  isLastFeed: false,
  initPlayRec: false,
});
const {
  feed,
  isLastFeed,
  initPlayRec,
  playIconClick,
  showRecInPlayPage,
} = toRefs(props);

const playerRef = ref<null | { player: VideoJsPlayer}>(null);
const progressRef = ref<HTMLElement>();
const currentTime = ref<number>(0);

// composable
const { getMiniAppPath } = useGetCommonMethods();
const swiperItemReport = userSwiperItemReport({ feed, playerRef, currentTime });
const { info: urlInfo } = useProvideUrlInfo();
const { useVideoEvent, emitVideoEvent } = useVideoEventHandler();
const dramaReport = useDramaReport();

// data
const isPlay = ref(false);
const hasPlay = ref(false);
const progress = ref(0);
const showPlayBtn = ref(true); // 是否展示播放按钮
const showDramaAct = ref<boolean>(false); // 是否展示短剧操作区域
const isDragging = ref<boolean>(false);
const timer = ref(null);

// state
const store = useStore();
const { state } = store;
const activeIndex = computed(() => state.activeIndex);
const dramaList = computed(() => state.dramaList || []);
const isCollection = computed(() => state.isCollection);
const isDrama = computed(() => state.isDrama);
const showCollection = computed(() => state.showCollection);
const isNewUI = computed(() => state.isNewUI);
const showHandTip = computed(() => state.showHandTip);
const errorMsg = computed(() => state.errorMsg);
const activeDrama = computed(() => dramaList.value[activeIndex.value]);
const showCover = computed(() => !hasPlay.value);
const totalTime = computed(() => Math.floor(feed.value.video.duration / 1000));
const playPercent = computed(() => {
  if (!feed.value.video.duration) return; // 避免初始时长为 0 时出错
  const duration = Math.floor(feed.value.video.duration / 1000);
  return (currentTime.value / duration) * 100;
});

// 是否初始化转评赞等互动区域
const actionWrapperExist = computed(() => {
  const { vtype } = urlInfo.query;
  if (isDrama.value || vtype === 'interactive') {
    return false;
  }
  return true;
});

const autoPlay = computed(() => {
  if (feed.value?.index === activeIndex.value && !errorMsg.value) {
    return true;
  }

  return false;
});
const videoOptions = computed(() => ({
  autoPlay: autoPlay.value,
  preload: 'meta',
}));

useVideoEvent((params) => {
  const player = playerRef.value?.player;
  if (!player) return;
  const isActive = params.index === feed.value?.index; // 是否是当前视频
  switch (params.eventName) {
    case EVideoEvtNameTypes.readyPlay:
      // 收到视频"准备播放"请求，其他视频需要 stop
      if (!isActive) {
        player.currentTime(0);
        player.pause();
      }
      break;
    // 收到视频"播放暂停切换"请求
    case EVideoEvtNameTypes.changePlayOrPause:
      if (isActive) {
        player.paused() ? player.play() : player.pause();
      }
      break;
    // 收到视频"播放"请求，其他视频需要 stop
    case EVideoEvtNameTypes.play:
      if (isActive) {
        player.play();
      } else {
        player.currentTime(0);
        player.pause();
      }
      break;
  };
});

// computed
const isActive = computed(() => feed.value?.index === activeIndex.value);
const showNewUI = computed(() => showCollection.value && isNewUI.value);
const initOldUI = computed(() => !isNewUI.value && isCollection.value);
const checkActStatus = computed(() => isPlay.value && showDramaAct.value);

const isCreatePlayer = computed(() => Math.abs(feed.value?.index - activeIndex.value) <= 2);
const miniAppPathClickPlay = computed(() => getMiniAppPath(EMiniAppPosition.video));

const getImage = (url: string) => `url(${fixHttps(url)})`;

const onPlay = () => {
  isPlay.value = true;

  if (!hasPlay.value) {
    hasPlay.value = true;
    swiperItemReport.onPlay(true);
  }

  swiperItemReport.onPlay();
};
const onPlaying = () => {
  // 展示精选集时不显示播放按钮
  // 播放后点击可展示播放按钮
  if (isActive.value) {
    store.commit(MutationTypes.SET_ACTIVE_PLAYER_PLAYING, { value: true });
  };
};
const onPause = () => {
  isPlay.value = false;
  if (isActive.value) {
    store.commit(MutationTypes.SET_ACTIVE_PLAYER_PLAYING, { value: false });
  };
  swiperItemReport.onPause();
};
const onTimeupdate = (timeProgress: number) => {
  if (!playerRef.value) return;
  progress.value = timeProgress;
  currentTime.value = playerRef.value.player.currentTime();
};
const onPreventAutoplay = () => {
  // 展示精选集时不显示播放按钮
  // 自动播放失败时展示给给用户播放按钮
  if (!showCollection.value) showPlayBtn.value = true;
};
const onClickPlayStatus = () => {
  if (showCollection.value) {
    store.commit(MutationTypes.SET_SHOW_COLLECTION, { value: false });
  }
};
const onEnded = () => {
  emits('on-ended');
  if (!playerRef.value) return;
  swiperItemReport.onEnded({
    isLastFeed: isLastFeed.value,
  });
};
const onClickCollection = () => {
  store.commit(MutationTypes.SET_SHOW_COLLECTION, { value: true });
};
const clickShowBtn = () => {
  store.commit(MutationTypes.SET_SHOW_COLLECTION, { value: !showCollection.value });
  swiperItemReport.clickShowBtn(showRecInPlayPage.value);
};
const clickDramaBtn = () => {
  emitVideoEvent({
    index: activeIndex.value,
    eventName: EVideoEvtNameTypes.changePlayOrPause,
  });
};
const clickDramaWrap = () => {
  showDramaAct.value = true;
};

const getTimeByPosition = (clientX) => {
  const rect = progressRef.value.getBoundingClientRect();
  const pos = (clientX - rect.left) / rect.width;
  const clampedPos = Math.max(0, Math.min(pos, 1)); // 限制在 0-1 范围内
  const duration = Math.floor(feed.value.video.duration / 1000);
  return clampedPos * duration;
};
const touchProgressStart = (e) => {
  const touch = e.touches[0];
  isDragging.value = true;
  const seekTime = getTimeByPosition(touch.clientX);
  const player = playerRef.value?.player;
  player.currentTime(seekTime);

  dramaReport.dramaMove();
};
const touchProgressMove = (e) => {
  const touch = e.touches[0];
  const seekTime = getTimeByPosition(touch.clientX);
  const player = playerRef.value?.player;
  player.currentTime(seekTime);
};
const touchProgressEnd = () => {
  isDragging.value = false;
};

watch(checkActStatus, (val) => {
  clearTimeout(timer.value);
  if (!val) {
    return;
  }

  timer.value = setTimeout(() => {
    showDramaAct.value = false;
    clearTimeout(timer.value);
  }, 4000);
});

watch(isDragging, (val) => {
  clearTimeout(timer.value);
  if (val || !isPlay.value) {
    return;
  }

  timer.value = setTimeout(() => {
    showDramaAct.value = false;
    clearTimeout(timer.value);
  }, 4000);
});

onMounted(() => {
  if (feed.value?.index === 0) {
    swiperItemReport.mountedReport();
  }
});

watch(activeIndex, () => {
  currentTime.value = 0;

  if (isActive.value) {
    swiperItemReport.mountedReport();
  };
});

watch(currentTime, (value) => {
  if (isCollection.value || isDrama.value) {  // 合集和短剧不出现手势
    return;
  }

  const { duration = 0 } = feed.value?.video;
  const fullTime = duration / 1000;
  if (fullTime <= 3) { // 视频时长低于3s，不出现手势弹窗
    return;
  }
  if (activeIndex.value === 0 && !showHandTip.value) { // 第一个视频 && 首次播放，才展示提示手势
    switch (true) {
      case (fullTime <= 30):
        if (value >= fullTime - 5) { // 视频时长在 15-30s，在倒数 5 秒出弹窗
          store.commit(MutationTypes.SET_SHOW_HAND_TIP, { value: true });
        }
        break;
      case (fullTime <= 60):
        if (value >= fullTime - 10) { // 视频时长在 30-60s，在倒数 10 秒出弹窗
          store.commit(MutationTypes.SET_SHOW_HAND_TIP, { value: true });
        }
        break;
      case (fullTime > 60):
        if (value >= fullTime * 0.8) { // 视频时长大于 60s，在播放完成度 80% 出弹窗
          store.commit(MutationTypes.SET_SHOW_HAND_TIP, { value: true });
        }
        break;
      default:
        break;
    }
  }
});

</script>

<style lang="scss" scoped>
.swiper-item,
.scale-item {
  width: 100%;
  height: 100%;
}
.swiper-item {
  position: relative;

  .drama-act-wrapper {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 100%;

    .drama-act {
      position: relative;
      width: 100%;
      height: 100%;
      background-image: linear-gradient(rgba(0, 0, 0, .6), rgba(0, 0, 0, .36) 20%,
      transparent 36%, transparent 70%, rgba(0, 0, 0, .24) 77%, rgba(0, 0, 0, .36) 83%, rgba(0, 0, 0, .6));

      .drama-cover {
        width: 100%;
        height: 100%;
        background-size: cover;
        background-position: center center;
      }
      .act-area {
        @include responsiveStyleSingle(bottom, 200, "px");
        @include responsiveStyleSingle(top, 200, "px");
        position: absolute;
        left: 0;
        width: 100%;
      }
      .drag-time {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate3d(-50%, -50%, 0);
        font-weight: 600;

        .current-time {
          @include responsiveStyleSingle(font-size, 32, "px");
        }
        .total-time {
          @include responsiveStyleSingle(font-size, 32, "px");
          color: rgba(255, 255, 255, 0.55);
        }
      }
    }

    .act-btn {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate3d(-50%, -50%, 0);

      &.play {
        background-image: url('https://isee.weishi.qq.com/share/icon_play_start.png');
        background-size: 100% auto;
        @include responsiveStyleSingle(width, 168, "px");
        @include responsiveStyleSingle(height, 175, "px");
      }

      &.pause {
        background-image: url('https://isee.weishi.qq.com/share/icon_play_pause.png');
        background-size: 100% auto;
        @include responsiveStyleSingle(width, 148, "px");
        @include responsiveStyleSingle(height, 164, "px");
      }
    }

    .show-detail {
      position: absolute;
      bottom: 0;
      @include responsiveStyleSingle(margin-left, 32, 'px');
      @include responsiveStyleSingle(margin-right, 32, 'px');
    }
    .name {
      @include responsiveStyleSingle(margin-bottom, 12, 'px');
      @include responsiveStyleSingle(font-size, 32, 'px');
      font-weight: 600;
    }

    .brief {
      @include responsiveStyleSingle(font-size, 28, 'px');
      @include responsiveStyleSingle(margin-bottom, 16, 'px');
      color: #A9A9A9;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .progress-wrap {
      display: flex;
      @include responsiveStyleSingle(padding-bottom, 20, 'px');

      .time-display {
        @include responsiveStyleSingle(font-size, 22, 'px');
        @include responsiveStyleSingle(margin-right, 16, 'px');
      }
      .progress-container {
        flex: 1;
        display: flex;
        align-items: center;
      }
      .progress {
        width: 100%;
        @include responsiveStyleSingle(height, 8, 'px');
        @include responsiveStyleSingle(border-radius, 4, 'px');
        background: #323f4a;
        position: relative;
        cursor: pointer;
      }

      /* 已播放进度条 */
      .progress-filled {
        height: 100%;
        background: #776BFF;
        @include responsiveStyleSingle(border-radius, 4, 'px');
        width: 0%;
      }

      /* 拖拽滑块 */
      .progress-handle {
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
        @include responsiveStyleSingle(padding, 5, 'px');
        background: white;
        border-radius: 50%;
      }
    }
  }
}

.scale-item {
  position: relative;
  transition: 0.3s ease;
  transform-origin: left 50%;
  transform: translateZ(0);
  &.show-collection {
    @include responsiveTransform(2.6667, 0, 0, 'vw');
    transform: scale(0.725);
  }
  .video-poster {
    height: 100%;
    width: 100%;
    position: absolute;
    margin: 0;
    padding: 0;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    height: 100%;
    background-repeat: no-repeat;
    background-position: 50% 50%;
    background-size: contain;
  }
  .tools-wrapper {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    bottom: constant(safe-area-inset-bottom);
    bottom: env(safe-area-inset-bottom);
    pointer-events: none;
    overflow: hidden;
    .animate {
      transform: translateZ(0);
    }
    .show-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: auto;
      @include responsiveStyleSingle(height, 14.9333, 'vw');
      div {
        display: flex;
        align-items: center;
        justify-content: center;
        @include responsiveStyleSingle(width, 24, 'vw');
        @include responsiveStyleSingle(height, 5.8667, 'vw');
        @include responsiveStyleSingle(font-size, 3.2, 'vw');
        @include responsiveStyleSingle(border-radius, 3.2, 'vw');
        background: rgba(59, 59, 59, 0.8);
      }
      i {
        display: block;
        @include responsiveStyleSingle(width, 5.3333, 'vw');
        @include responsiveStyleSingle(height, 5.3333, 'vw');
        background: url('https://qzonestyle.gtimg.cn/aoi/sola/20200302114737_YPEVFPjpOL.png');
        background-size: 100% 100%;
        &.show {
          background: url('https://qzonestyle.gtimg.cn/aoi/sola/20200227200838_kaBS0Wm8a5.png');
          background-size: 100% 100%;
        }
      }
    }
  }
  .fade-enter-active,
  .fade-leave-active {
    transition: all 0.3s;
    transition-timing-function: cubic-bezier(0, 0, 0.19, 0.97) !important;
  }
  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
  :deep(.mask) {
    transform: translateZ(0);
  }
}
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s;
}
.slide-enter,
.slide-leave-to {
  opacity: 0;
  transform: translate3d(0, 100%, 0);
}
</style>
